/**
 * EvidenceConflictsPanel — EDX-10.
 *
 * Site Evidence top-page surface for resolving unresolved evidence
 * conflicts inline. Mounted between CoverageMatrix and SEDVariableBrowser.
 *
 * Why it exists: pre-EDX-10 the report's high-conflicts CTA (then on
 * `ReportAlertBanner`; since 2026-10-13 the report header's ⋮ "Review
 * evidence conflicts" entry, which deep-links here with
 * `focus=conflicts`) landed users on the Site Evidence tab top with NO
 * conflicts UI visible — the only one was `EvidenceConflictDialog`
 * inside `SEDSectionEditor` (4-5 clicks deep; both since deleted). Users on
 * Redhead's report saw "6 unresolved" persist across page refreshes
 * and concluded the resolution flow was broken. Investigation
 * confirmed the SED-side `applyConflictResolution` + `persistSedConflicts`
 * chain works correctly; what was missing was an entry point from the
 * Site Evidence tab.
 *
 * Design:
 *   - Reads `(sed.evidenceConflicts ?? []).filter(c => !c.userResolution)`
 *     — same predicate as `countUnresolvedConflicts` so the count
 *     here can never disagree with the report header's status chip.
 *   - Groups by sectionId for readability; high → medium → low within
 *     each group.
 *   - Inline `[Resolve ▾]` dropdown per row reuses the existing
 *     `applyConflictResolution` + `persistSedConflicts` chain — no
 *     new backend, no duplicate logic.
 *   - On resolve: optimistically removes the row from local state,
 *     calls `onResolved` so the parent invalidates the live SED cache
 *     + the report's conflicts state re-derives.
 *
 * Reproducibility invariant preserved: `report.unresolvedConflicts`
 * (stamped at gen) is untouched here. The panel READS the live SED;
 * resolutions write ONLY to `site.sed.evidenceConflicts`. The
 * stamped audit trail on the report stays intact, per SC2.
 *
 * `focusOnMount=true` (used when the user arrived via the banner's
 * `?focus=conflicts` URL param) scrolls the first row into view and
 * paints a 2.8-second ring so the eye lands on the resolution
 * affordance.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Check, ExternalLink, Layers } from 'lucide-react';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import type { EvidenceConflict, ConflictResolution, ConflictSeverity } from '../../../services/evidenceConflict';
import type { SiteDocument } from '../../../types/siteRag';
import { groupConflictsByField, type FieldConflictGroup } from '../../../lib/conflictGrouping';
import { applyBulkResolution, type BulkStrategy } from '../../../lib/bulkConflictResolve';
import {
  loadPresets,
  projectPresetMatches,
  type ConflictResolutionPreset,
  type PresetMatch,
} from '../../../lib/conflictResolutionPresets';
import { toast } from 'sonner';

export interface EvidenceConflictsPanelProps {
  /** Live SED (current state, NOT the gen-time snapshot on the report). */
  sed: SiteEvidenceDocument | null;
  /** Optional exact SED field paths (registry or variable-browser shape).
   * A section path includes its subtree; [] includes nothing. */
  scopedFieldPaths?: readonly string[];
  /** Optional section boundary, intersected with scopedFieldPaths when both
   * are supplied. Omitted scope preserves the full site-wide surface. */
  scopedSectionIds?: readonly string[];
  /** Used for the persist call below. */
  siteId: string;
  /**
   * #sed-owner-path — the owning site's canonical `sedStoragePath`. Threaded
   * into `persistSedConflicts` so the write lands on the object the read side
   * loads: a SED lives under the site OWNER's namespace, so on a
   * company-shared (or legacy flat-path) site a caller-built
   * `users/{uid}/sites/…` key writes resolutions into the CALLER's namespace
   * and the conflicts reappear on the next load. Optional only for the
   * own-site case, where the resolver's fallback is identical.
   */
  sedStoragePath?: string;
  /** True when the user arrived here via the banner `?focus=conflicts`.
   *  Triggers the scroll-into-view + ring-highlight effect on the
   *  first unresolved row. */
  focusOnMount?: boolean;
  /** Called after a resolution lands (post-persist) so the parent can
   *  invalidate its live SED cache + the banner re-derives. The panel
   *  also optimistically drops the resolved row from local state so
   *  the user sees the immediate effect even before the parent
   *  re-renders. */
  onResolved?: () => void;
  /**
   * UFC-1 / UFC-3 — live SiteDocument list. Used to resolve `docId →
   * fileName` for the per-document disclosure under each grouped row.
   * Optional; the panel falls back to the conflict's stamped `docName`
   * when omitted (legacy SEDs without a live doc list passed in).
   */
  documents?: readonly SiteDocument[];
  /**
   * UFC-3 — Conflicts panel → SED Variable Browser cross-link.
   * Called when the user clicks "[View variable →]" on a grouped row.
   * The parent typically sets `?focus=variable:<sectionId>.<fieldKey>`
   * and scrolls the matching SED Variable Browser row into view.
   * Optional — when omitted the cross-link is hidden so legacy mounts
   * (or test harnesses without a parent router) don't render a dead
   * button.
   */
  onViewVariable?: (sectionId: string, fieldKey: string) => void;
  /**
   * UFC-3 — group-focus key set by the SED Variable Browser's
   * reciprocal "[Resolve →]" chip (`?focus=variable:<sectionId>.<field>`).
   * The panel scrolls + ring-highlights the matching group when this
   * is present. Optional — null/undefined preserves the
   * `focusOnMount`-only behaviour from EDX-10.
   */
  focusGroupKey?: string | null;
}

/** Scope display rows only; persisted identities and values stay canonical.
 * Registry paths and variable-browser paths differ only by the data wrapper.
 * A section-only path scopes its subtree. Explicit empty arrays match nothing.
 */
function pathMatchesTopicScope(
  path: string,
  fieldPaths?: readonly string[],
  sectionIds?: readonly string[],
): boolean {
  const normalize = (value: string) => value.trim().replace(/^([^.]+)\.data\./, '$1.');
  const normalized = normalize(path);
  const sectionId = normalized.split('.')[0];
  if (sectionIds != null && !sectionIds.some(id => id.trim() === sectionId)) return false;
  if (fieldPaths == null) return true;
  return fieldPaths.some(value => {
    const scope = normalize(value);
    return scope.length > 0 && (normalized === scope || normalized.startsWith(`${scope}.`));
  });
}

const SEVERITY_RANK: Record<ConflictSeverity, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

const SEVERITY_CHIP: Record<ConflictSeverity, string> = {
  high:   'bg-danger/10 text-danger border-danger/40',
  medium: 'bg-warning/10 text-warning border-warning/40',
  low:    'bg-state-hover text-text-muted border-border',
};

const RESOLVE_LABEL: Record<ConflictResolution, string> = {
  'prefer-doc':      'Use document',
  'prefer-provider': 'Use provider',
  'prefer-report':   'Use prior report',
  'manual-review':   'Mark for review',
};

/** Truncate to ~120 chars with a graceful ellipsis. */
function truncate(s: string, n = 120): string {
  if (!s || s.length <= n) return s ?? '';
  return s.slice(0, n).trimEnd() + '…';
}

/**
 * ICR-2 — render a value for the side-by-side comparison cell.
 * Booleans / numbers stringify; strings come through verbatim; objects
 * round-trip through JSON. Trims any leading/trailing whitespace so a
 * cleanly-extracted `" AE "` doesn't render with awkward padding.
 */
function formatValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v.trim() || '—';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * ICR-2 — camelCase / snake_case → "Camel Case" / "Snake Case" with the
 * first letter capitalised. Drives the row title + the Confirm button
 * copy. Falls back to the raw field key when no transform applies.
 */
function humanizeField(field: string): string {
  if (!field) return field;
  const spaced = field
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return field;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const EvidenceConflictsPanel: React.FC<EvidenceConflictsPanelProps> = ({
  sed,
  scopedFieldPaths,
  scopedSectionIds,
  siteId,
  sedStoragePath,
  focusOnMount,
  onResolved,
  documents,
  onViewVariable,
  focusGroupKey,
}) => {
  // Local copy of conflicts so the optimistic-remove on resolve doesn't
  // wait for the parent's SED re-fetch. The effect below re-syncs from
  // the prop whenever the live SED changes.
  const [conflicts, setConflicts] = useState<EvidenceConflict[]>(() =>
    sed?.evidenceConflicts ?? [],
  );
  // UFC-2 — per-GROUP "in-flight" so the dropdown can't fire twice and
  // the user sees a single "Saving…" state across the (potentially N)
  // underlying conflicts the group resolves atomically.
  const [resolving, setResolving] = useState<Set<string>>(() => new Set());
  // UF-1 — header-level bulk-resolve in-flight + dropdown open state.
  // Distinct from `resolving` (per-group) so a single click on the
  // header dropdown locks every CTA + dims every row while the batch
  // is mid-flight without scattering per-group flags.
  const [bulkResolving, setBulkResolving] = useState(false);
  const [bulkDropdownOpen, setBulkDropdownOpen] = useState(false);
  // UF-2 (UFC-FOLLOWUP-PRESET-RULES) — preset library. Loaded once at
  // mount from localStorage (built-ins + user-saved). `activePreset`
  // is the preview-selected one — null means "no preset previewed".
  // Application is explicit (HARD-RULE-9 — never auto-applies); the
  // user clicks "Apply preset" to fold the matched groups through
  // `applyBulkResolution` (UF-1 batch primitive).
  const [presets, setPresets] = useState<ConflictResolutionPreset[]>([]);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [presetMenuOpen, setPresetMenuOpen] = useState(false);
  // Hydrate presets from storage. Idempotent; safe to re-run on remount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { auth } = await import('../../../firebase');
        const uid = auth.currentUser?.uid ?? null;
        if (cancelled) return;
        setPresets(loadPresets(uid));
      } catch {
        if (!cancelled) setPresets(loadPresets(null));
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // Per-group dropdown open state (component-local; not URL-persisted).
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  // UFC-2 — per-group "Use document…" picker open state. Triggered
  // when the group has ≥2 doc values that differ (R2 mitigation —
  // force a choice instead of silently picking the first).
  const [openDocPicker, setOpenDocPicker] = useState<string | null>(null);
  // UFC-2 — per-group disclosure (per-doc detail) collapsed by default.
  const [expandedGroupDetail, setExpandedGroupDetail] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    setConflicts(sed?.evidenceConflicts ?? []);
  }, [sed]);

  // Preserve the full canonical array for each existing one-persist write.
  // Only the displayed/actionable subset is scoped; bulk/preset targets derive
  // from it, so resolving a topic cannot resolve or drop another topic.
  const scopedConflicts = useMemo(() => conflicts.filter(conflict => {
    const path = conflict.field.startsWith(`${conflict.sectionId}.`)
      ? conflict.field : `${conflict.sectionId}.${conflict.field}`;
    return pathMatchesTopicScope(path, scopedFieldPaths, scopedSectionIds);
  }), [conflicts, scopedFieldPaths, scopedSectionIds]);

  // Filter + sort. Group by section, then sort high → medium → low
  // within each group; sections themselves are alphabetised so the
  // panel reads stably across renders.
  const unresolved = useMemo(
    // ICR-1 — exclude prefilter-auto-resolved rows from the main list.
    // They still ride along on the SED for audit; the disclosure below
    // renders them with the reason string.
    () => scopedConflicts.filter(c => !c.userResolution && !c.autoResolved),
    [scopedConflicts],
  );

  // ICR-3 — auto-resolved disclosure. Walks the same conflict list but
  // surfaces ONLY rows the ICR-1 prefilter machine-resolved, so the
  // user can audit the prefilter's decisions without being burdened by
  // them on the primary list.
  const autoResolved = useMemo(
    () => scopedConflicts.filter(c => c.autoResolved),
    [scopedConflicts],
  );

  // UFC-1 / UFC-2 — single source of truth for the panel's rendered
  // groups. `groupConflictsByField` collapses every (sectionId, field,
  // docId) triplet onto a single (sectionId, fieldKey) group whose
  // members carry the per-doc detail. Cheap to recompute; memoised on
  // the unresolved-conflict slice + the live SiteDocument list (for
  // filename resolution).
  const fieldGroups = useMemo(
    () => groupConflictsByField(unresolved, documents),
    [unresolved, documents],
  );

  // UF-2 — preset projection. Maps every `groupKey` whose
  // (sectionId, fieldKey) matches a rule in the active preset to the
  // strategy that would be applied. Drives:
  //   - per-row "Auto-resolve (preset)" badge (advisory; nothing
  //     resolves until the user explicitly clicks Apply).
  //   - the Apply-preset button's count + tooltip.
  //   - the per-row CTA disabled state (graying out the dropdown when
  //     the preset already covers the row would mislead — we leave
  //     the CTA enabled so the user can override per row; the badge
  //     is the only visible difference).
  const activePreset = useMemo(
    () => presets.find(p => p.id === activePresetId) ?? null,
    [presets, activePresetId],
  );
  const presetMatches = useMemo(
    () => projectPresetMatches(activePreset, fieldGroups),
    [activePreset, fieldGroups],
  );
  // UFC-2 — bucket groups by sectionId so the panel keeps the EDX-10
  // "collapse by section" affordance. Within a section, groups sort by
  // worst-severity descending so the user lands on the highest-stakes
  // decision first (same UX cadence as the pre-UFC per-conflict sort).
  const groupedSections = useMemo(() => {
    const bySection = new Map<string, FieldConflictGroup[]>();
    for (const g of fieldGroups) {
      const arr = bySection.get(g.sectionId) ?? [];
      arr.push(g);
      bySection.set(g.sectionId, arr);
    }
    for (const arr of bySection.values()) {
      arr.sort((a, b) => SEVERITY_RANK[b.worstSeverity] - SEVERITY_RANK[a.worstSeverity]);
    }
    return Array.from(bySection.entries())
      .sort(([a], [b]) => a.localeCompare(b));
  }, [fieldGroups]);

  // Per-section collapse state. All sections expanded by default — the
  // user landed here to resolve conflicts, not to navigate a tree.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set());

  // Focus-on-mount: scroll + highlight the first unresolved row 250 ms
  // after mount (lets the panel render + the SED prop hydrate). Same
  // animation cadence SET-B doc-focus uses.
  const firstRowRef = useRef<HTMLLIElement | null>(null);
  // UFC-3 — when the cross-link from the SED Variable Browser arrives
  // with a `focusGroupKey`, the panel re-binds `firstRowRef` to the
  // matching group's <li> (see render below) so the same scroll +
  // ring-highlight effect lands on the requested group, not the
  // top row.
  useEffect(() => {
    if (!focusOnMount && !focusGroupKey) return;
    if (unresolved.length === 0) return;
    const handle = window.setTimeout(() => {
      const el = firstRowRef.current;
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-warning', 'ring-offset-2');
        window.setTimeout(() => {
          el.classList.remove('ring-2', 'ring-warning', 'ring-offset-2');
        }, 2800);
      } catch { /* best-effort */ }
    }, 250);
    return () => window.clearTimeout(handle);
    // We only want this on mount + when conflicts/focus first appear.
    // Subsequent re-renders shouldn't re-highlight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOnMount, focusGroupKey, unresolved.length > 0]);

  /**
   * UFC-2 — resolve EVERY underlying conflict in a `(sectionId,
   * fieldKey)` group with the same resolution kind, then write the
   * SED ONCE (R1 mitigation — atomic-from-the-user's-perspective).
   *
   * For a 5-doc Acres group, this calls `applyConflictResolution` five
   * times against the local conflict list (pure projection — each call
   * returns a new array with one more entry stamped) and persists ONCE
   * with the final array. If the persist fails, every member rolls
   * back together; if it succeeds, all 5 rows vanish atomically.
   *
   * `pickedDocId` is optional — used by the "Use document…" picker when
   * the group has ≥2 disagreeing doc values (R2 mitigation). When
   * supplied, only the underlying conflict for that docId is stamped
   * `'prefer-doc'`; the others land on `'prefer-provider'` (we go with
   * the user's chosen doc value AND record that the provider lost the
   * pick for the others). For groups where every doc agrees on the
   * same value, the picker is skipped — the caller passes `undefined`
   * and all members are stamped uniformly.
   *
   * Authored override (`resolution: 'manual-review'`) marks every
   * member as `manual-review` so the row disappears (R3 mitigation —
   * authored beats both provider and doc).
   */
  const handleResolveGroup = async (
    group: FieldConflictGroup,
    resolution: ConflictResolution,
    pickedDocId?: string,
  ) => {
    if (resolving.has(group.key)) return;
    setResolving(prev => new Set(prev).add(group.key));
    setOpenDropdown(null);
    setOpenDocPicker(null);
    try {
      const { applyConflictResolution, persistSedConflicts } = await import('../../../services/evidenceConflict');
      const { auth } = await import('../../../firebase');
      const user = auth.currentUser;
      const resolvedBy = user?.uid ?? 'unknown';

      // R1 — single batch: fold every member through
      // applyConflictResolution against the LOCAL conflict list, then
      // persist ONCE with the final array. Either all 5 land or none do.
      let next = conflicts;
      for (const m of group.members) {
        // When the user picked a specific doc from the picker, only
        // THAT doc's underlying conflict carries `prefer-doc` — the
        // others are stamped `prefer-provider` so the resolution
        // captures "trust this doc's value for the field, not the
        // others".
        const memberResolution: ConflictResolution =
          pickedDocId !== undefined && resolution === 'prefer-doc'
            ? (m.docId === pickedDocId ? 'prefer-doc' : 'prefer-provider')
            : resolution;
        next = applyConflictResolution(
          next,
          { sectionId: group.sectionId, field: group.fieldKey, docId: m.docId },
          memberResolution,
          resolvedBy,
        );
      }

      // Optimistic update before persist so the entire group vanishes
      // in one paint frame.
      setConflicts(next);

      if (user && sed) {
        await persistSedConflicts(user.uid, siteId, sed, next, sedStoragePath);
      }
      // Notify mounted banners that the live SED changed so they
      // re-derive their counts.
      try {
        window.dispatchEvent(new CustomEvent('sed-conflicts-updated'));
      } catch { /* SSR / test envs without window */ }
      onResolved?.();
      toast.success(
        group.members.length > 1
          ? `Resolved ${group.members.length} sources for ${group.fieldLabel}.`
          : 'Conflict resolution saved.',
      );
    } catch (e) {
      console.warn('[EvidenceConflictsPanel] persist failed:', (e as Error)?.message);
      toast.error('Saved locally — Firestore persist failed.');
    } finally {
      setResolving(prev => {
        const n = new Set(prev);
        n.delete(group.key);
        return n;
      });
    }
  };

  /**
   * UF-1 (UFC-FOLLOWUP-BULK-RESOLVE) — resolve EVERY currently-visible
   * group with the same strategy and write the SED ONCE (HARD-RULE-8,
   * atomic batch). Same one-persist contract as `handleResolveGroup`,
   * just one level deeper: `applyBulkResolution` folds N groups (each
   * with M members) into one updated array; we persist that array in a
   * single `persistSedConflicts` call.
   *
   * Strategies:
   *   - 'prefer-provider'  — every group stamped prefer-provider.
   *   - 'prefer-doc-first' — first doc per group wins prefer-doc; the
   *     rest in that group land prefer-provider. Groups without any
   *     doc-side values fall back to prefer-provider (the helper has
   *     the rationale).
   *   - 'manual-review'    — every group stamped manual-review.
   *
   * Local conflict state is mutated optimistically BEFORE the persist
   * (one paint frame); a failed persist keeps the optimistic state
   * resident and surfaces an error toast. Same UX contract as the
   * single-group flow so the two surfaces stay symmetric.
   */
  const handleBulkResolve = async (strategy: BulkStrategy) => {
    if (bulkResolving) return;
    if (fieldGroups.length === 0) return;
    setBulkResolving(true);
    setBulkDropdownOpen(false);
    setOpenDropdown(null);
    setOpenDocPicker(null);
    try {
      const { applyConflictResolution, persistSedConflicts } = await import('../../../services/evidenceConflict');
      const { auth } = await import('../../../firebase');
      const user = auth.currentUser;
      const resolvedBy = user?.uid ?? 'unknown';

      // Single fold across ALL visible groups → one final conflict
      // array → one persist. If any sub-group's stamping throws the
      // whole batch unwinds (the catch below restores nothing because
      // we haven't touched setConflicts yet at the throw site, and we
      // only persist after the fold completes).
      const { next, summary } = applyBulkResolution(
        conflicts,
        fieldGroups,
        strategy,
        resolvedBy,
        applyConflictResolution,
      );

      setConflicts(next);

      if (user && sed) {
        await persistSedConflicts(user.uid, siteId, sed, next, sedStoragePath);
      }
      try {
        window.dispatchEvent(new CustomEvent('sed-conflicts-updated'));
      } catch { /* SSR / test envs without window */ }
      onResolved?.();
      toast.success(
        `Resolved ${summary.groupsResolved} group${summary.groupsResolved === 1 ? '' : 's'} (${summary.membersStamped} source${summary.membersStamped === 1 ? '' : 's'}).`,
      );
    } catch (e) {
      console.warn('[EvidenceConflictsPanel] bulk persist failed:', (e as Error)?.message);
      toast.error('Saved locally — Firestore persist failed.');
    } finally {
      setBulkResolving(false);
    }
  };

  /**
   * UF-2 — apply the active preset. Walks the projected match map,
   * partitions groups by strategy (since a preset can mix strategies
   * across rules), folds them through `applyBulkResolution` once per
   * strategy bucket against the SAME running array, then persists
   * ONCE (HARD-RULE-8 — atomic batch is preserved).
   *
   * `presetMatches` is a `Map<groupKey, PresetMatch>` so the lookup
   * stays O(1) per group; partitioning by strategy keeps the helper
   * uniform (it accepts one strategy at a time, by design).
   *
   * No-op when:
   *   - No preset is active.
   *   - The preset matches zero groups.
   * Both are quiet (no toast) — the user can re-select if they want
   * a different one.
   */
  const handleApplyPreset = async () => {
    if (bulkResolving) return;
    if (!activePreset) return;
    if (presetMatches.size === 0) return;
    setBulkResolving(true);
    setPresetMenuOpen(false);
    setBulkDropdownOpen(false);
    setOpenDropdown(null);
    setOpenDocPicker(null);
    try {
      const { applyConflictResolution, persistSedConflicts } = await import('../../../services/evidenceConflict');
      const { auth } = await import('../../../firebase');
      const user = auth.currentUser;
      const resolvedBy = user?.uid ?? 'unknown';

      // Partition the matched groups by strategy so we can fold each
      // bucket through `applyBulkResolution` with its own strategy
      // arg. The buckets share the running `next` array so the final
      // persist is still ONE write.
      const buckets = new Map<BulkStrategy, FieldConflictGroup[]>();
      for (const g of fieldGroups) {
        const m = presetMatches.get(g.key);
        if (!m) continue;
        const arr = buckets.get(m.strategy) ?? [];
        arr.push(g);
        buckets.set(m.strategy, arr);
      }

      let next = conflicts;
      let totalStamped = 0;
      let totalGroups = 0;
      for (const [strategy, gs] of buckets.entries()) {
        const r = applyBulkResolution(next, gs, strategy, resolvedBy, applyConflictResolution);
        next = r.next;
        totalStamped += r.summary.membersStamped;
        totalGroups += r.summary.groupsResolved;
      }

      setConflicts(next);

      if (user && sed) {
        await persistSedConflicts(user.uid, siteId, sed, next, sedStoragePath);
      }
      try {
        window.dispatchEvent(new CustomEvent('sed-conflicts-updated'));
      } catch { /* SSR / test envs without window */ }
      onResolved?.();
      toast.success(
        `Preset "${activePreset.label}" resolved ${totalGroups} group${totalGroups === 1 ? '' : 's'} (${totalStamped} source${totalStamped === 1 ? '' : 's'}).`,
      );
      // Clear the active preset after a successful apply — leaving
      // it set would re-paint the now-empty match map and confuse
      // the eye.
      setActivePresetId(null);
    } catch (e) {
      console.warn('[EvidenceConflictsPanel] preset apply failed:', (e as Error)?.message);
      toast.error('Saved locally — Firestore persist failed.');
    } finally {
      setBulkResolving(false);
    }
  };

  // No SED loaded yet — render nothing (the panel doesn't reserve a
  // skeleton; the rest of the page covers the visual budget).
  if (!sed) return null;

  // EMPTY state — keeps the slot reserved so the page doesn't jump
  // when the last conflict gets resolved.
  if (unresolved.length === 0) {
    return (
      <section
        className="rounded-lg border border-success/30 bg-success/5 px-3 py-2 flex items-center gap-2"
        data-testid="evidence-conflicts-panel-empty"
      >
        <Check className="w-3 h-3 text-success shrink-0" aria-hidden="true" />
        <span className="type-meta text-success">
          No unresolved conflicts{scopedFieldPaths != null || scopedSectionIds != null ? ' in this topic' : ''}
        </span>
        <span className="text-[10px] text-text-secondary">
          {scopedFieldPaths != null || scopedSectionIds != null ? 'Other topics are reviewed separately.' : 'SED is clean.'}
        </span>
      </section>
    );
  }

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  // Render. Use a deterministic id for the very first row across the
  // groupedSections so the focus-on-mount scroll target is stable.
  // UFC-3 — when `focusGroupKey` is set, the ref attaches to that
  // specific group instead of the first one so the scroll/highlight
  // effect lands where the cross-link asked.
  let firstRowAssigned = !!focusGroupKey;

  return (
    <section
      className="space-y-2"
      data-testid="evidence-conflicts-panel"
    >
      <header className="flex items-start justify-between gap-2 pb-1 border-b border-border-subtle">
        <div className="space-y-0.5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3 text-warning" aria-hidden="true" />
            Unresolved evidence conflicts
            <span
              className="type-body font-mono uppercase tracking-widest px-1.5 py-0.5 border border-warning/40 text-warning"
              data-testid="evidence-conflicts-panel-count"
            >
              {unresolved.length}
            </span>
          </div>
          <div className="type-body text-text-muted">
            Pick a resolution per row — provider value, document value, or mark for review. Saved instantly.
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
        {/* UF-2 (UFC-FOLLOWUP-PRESET-RULES) — preset library trigger.
            Surfaces the canned rule sets ("always prefer provider
            for regulatory.*", etc.) so the user can pre-apply
            resolution choices without dropdowns row-by-row. The
            menu's badge ("N match") previews the count BEFORE
            commit (HARD-RULE-9 — advisory). Apply CTA only renders
            once a preset is selected AND ≥1 group matches. */}
        {fieldGroups.length > 0 && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setPresetMenuOpen(prev => !prev)}
              disabled={bulkResolving}
              aria-haspopup="menu"
              aria-expanded={presetMenuOpen}
              data-testid="evidence-conflicts-panel-preset-trigger"
              className="type-body uppercase tracking-widest border border-border text-text-secondary hover:text-primary hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed px-1.5 py-0.5 inline-flex items-center gap-1"
            >
              {activePreset ? `Preset: ${activePreset.label}` : 'Presets'}
              <ChevronDown className="w-2.5 h-2.5" aria-hidden="true" />
            </button>
            {presetMenuOpen && !bulkResolving && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-0.5 z-10 border border-border bg-background shadow-lg min-w-[280px] max-h-72 overflow-y-auto"
                data-testid="evidence-conflicts-panel-preset-menu"
              >
                {/* "None" — clears the preview. */}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setActivePresetId(null); setPresetMenuOpen(false); }}
                  data-testid="evidence-conflicts-panel-preset-clear"
                  className="w-full text-left text-[10px] px-2 py-1 hover:bg-state-hover focus-visible:outline-none focus-visible:bg-state-hover border-b border-border-subtle"
                >
                  <span className="text-text-muted italic">(no preset)</span>
                </button>
                {presets.map(p => {
                  const matchCount = projectPresetMatches(p, fieldGroups).size;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="menuitem"
                      onClick={() => { setActivePresetId(p.id); setPresetMenuOpen(false); }}
                      title={p.description ?? p.label}
                      data-testid={`evidence-conflicts-panel-preset-select-${p.id}`}
                      className="w-full text-left text-[10px] px-2 py-1 hover:bg-state-hover focus-visible:outline-none focus-visible:bg-state-hover"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-text-primary truncate">{p.label}</span>
                        <span className="shrink-0 type-meta uppercase tracking-widest text-text-muted">
                          {matchCount} match{matchCount === 1 ? '' : 'es'}
                          {p.scope === 'company' && ' · built-in'}
                        </span>
                      </div>
                      {p.description && (
                        <div className="type-body text-text-muted mt-0.5 line-clamp-2">{p.description}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {/* UF-2 — Apply CTA. Renders alongside the preset chip when
            ≥1 group matches the active preset. Folds the matched
            groups through `applyBulkResolution` (UF-1 primitive) and
            persists ONCE. */}
        {activePreset && presetMatches.size > 0 && (
          <button
            type="button"
            onClick={handleApplyPreset}
            disabled={bulkResolving}
            data-testid="evidence-conflicts-panel-preset-apply"
            className="type-body uppercase tracking-widest rounded-md border border-success/50 bg-success/10 text-success hover:bg-success/20 disabled:opacity-50 disabled:cursor-not-allowed px-1.5 py-0.5"
            aria-label={`Apply ${activePreset.label} to ${presetMatches.size} matched groups`}
          >
            {bulkResolving ? 'Applying…' : `Apply (${presetMatches.size})`}
          </button>
        )}
        {/* UF-1 (UFC-FOLLOWUP-BULK-RESOLVE) — header-level "Resolve all
            (N)" affordance. Folds every visible group through
            `applyBulkResolution` and persists ONCE (HARD-RULE-8 — single
            Firestore batch). Hidden when nothing to resolve. */}
        {fieldGroups.length > 1 && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setBulkDropdownOpen(prev => !prev)}
              disabled={bulkResolving}
              aria-haspopup="menu"
              aria-expanded={bulkDropdownOpen}
              data-testid="evidence-conflicts-panel-bulk-resolve-trigger"
              className="type-body uppercase tracking-widest rounded-md border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed px-1.5 py-0.5 inline-flex items-center gap-1"
            >
              <Layers className="w-2.5 h-2.5" aria-hidden="true" />
              {bulkResolving ? 'Resolving…' : `Resolve all (${fieldGroups.length})`}
              <ChevronDown className="w-2.5 h-2.5" aria-hidden="true" />
            </button>
            {bulkDropdownOpen && !bulkResolving && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-0.5 z-10 border border-border bg-background shadow-lg min-w-[240px]"
                data-testid="evidence-conflicts-panel-bulk-resolve-menu"
              >
                {(['prefer-provider', 'prefer-doc-first', 'manual-review'] as BulkStrategy[]).map(s => (
                  <button
                    key={s}
                    type="button"
                    role="menuitem"
                    onClick={() => handleBulkResolve(s)}
                    className="w-full text-left text-[10px] px-2 py-1 hover:bg-state-hover focus-visible:outline-none focus-visible:bg-state-hover"
                    data-testid={`evidence-conflicts-panel-bulk-resolve-${s}`}
                  >
                    {s === 'prefer-provider' && 'Confirm all provider values'}
                    {s === 'prefer-doc-first' && 'Use first document for each'}
                    {s === 'manual-review' && 'Mark all for review'}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
      </header>

      {/* ICR-3 — auto-resolved disclosure. Surfaces machine-resolved
          conflicts so the user can audit the prefilter's decisions
          without being burdened by them on the primary list. */}
      {autoResolved.length > 0 && (
        <details
          className="rounded-md border border-success/30 bg-success/5 px-2 py-1"
          data-testid="evidence-conflicts-panel-auto-resolved"
        >
          <summary
            className="cursor-pointer type-meta text-success"
            data-testid="evidence-conflicts-panel-auto-resolved-summary"
          >
            ✓ {autoResolved.length} auto-resolved (obvious doc-extraction errors)
          </summary>
          <ul className="mt-1 space-y-0.5 pl-3">
            {autoResolved.map(c => (
              <li
                key={`${c.sectionId}::${c.field}::${c.docId}`}
                className="text-[10px] text-text-muted"
                data-testid={`evidence-conflicts-panel-auto-resolved-row-${c.sectionId}-${c.field}`}
              >
                <span className="font-medium text-text-secondary">{humanizeField(c.field)}</span>
                {': kept provider '}
                <span className="font-mono">{formatValue(c.providerValue)}</span>
                {' — doc was '}
                <span className="font-mono">"{formatValue(c.docValue)}"</span>
                {c.autoResolvedReason && (
                  <span className="text-text-muted"> ({c.autoResolvedReason})</span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      <ul
        className="space-y-3"
        data-testid="evidence-conflicts-panel-sections"
      >
        {groupedSections.map(([sectionId, rows]) => {
          const collapsed = collapsedSections.has(sectionId);
          return (
            <li
              key={sectionId}
              data-testid={`evidence-conflicts-panel-section-${sectionId}`}
            >
              <button
                type="button"
                onClick={() => toggleSection(sectionId)}
                aria-expanded={!collapsed}
                className="w-full flex items-center gap-1.5 type-section text-text-secondary hover:text-text-primary"
                data-testid={`evidence-conflicts-panel-section-toggle-${sectionId}`}
              >
                {collapsed ? (
                  <ChevronRight className="w-3 h-3" aria-hidden="true" />
                ) : (
                  <ChevronDown className="w-3 h-3" aria-hidden="true" />
                )}
                <span>{sectionId}</span>
                <span className="type-body uppercase tracking-widest text-text-muted">
                  · {rows.length} field{rows.length === 1 ? '' : 's'} ·{' '}
                  {rows.reduce((sum, g) => sum + g.members.length, 0)} source{rows.reduce((sum, g) => sum + g.members.length, 0) === 1 ? '' : 's'}
                </span>
              </button>
              {!collapsed && (
                <ul
                  className="mt-1 space-y-1"
                  data-testid={`evidence-conflicts-panel-rows-${sectionId}`}
                >
                  {rows.map(g => {
                    const dropdownOpen = openDropdown === g.key;
                    const docPickerOpen = openDocPicker === g.key;
                    const detailOpen = expandedGroupDetail.has(g.key);
                    const isResolving = resolving.has(g.key);
                    const assignFirst = !firstRowAssigned;
                    if (assignFirst) firstRowAssigned = true;
                    // UFC-2 — preserve ICR-2 / ICR-4 / RDX-6 axis flags
                    // at the GROUP level. `hasAuthored` / `hasReport` are
                    // already aggregated by groupConflictsByField; the
                    // showConfirm-* gates fall through unchanged.
                    const hasAuthored = g.hasAuthored;
                    const hasReport = g.hasReport;
                    const showConfirmProvider =
                      !hasAuthored && !hasReport && g.authoritative === 'provider' && !isResolving;
                    const showConfirmDoc =
                      !hasAuthored && !hasReport && g.authoritative === 'document' && !isResolving;
                    // UFC-2 / R2 — distinct doc values across members.
                    // When every member agrees on the same doc value,
                    // the "Use document…" picker is skipped; the
                    // resolveGroup call stamps every member uniformly.
                    const distinctDocValues = Array.from(
                      new Set(
                        g.members
                          .filter(m => m.docValue !== undefined && m.docValue !== '')
                          .map(m => JSON.stringify(m.docValue)),
                      ),
                    );
                    const hasMultipleDocValues = distinctDocValues.length > 1;
                    const firstDocValue = g.members.find(m => m.docValue !== undefined)?.docValue;
                    // UFC-3 — group focus highlight (when the SED
                    // Variable Browser's reciprocal chip drove the user
                    // back to the panel). One paint frame of ring +
                    // smooth scroll, same cadence as focusOnMount.
                    const isCrossLinkFocused = focusGroupKey === g.key;
                    // UF-2 — per-group preset match. Drives the
                    // "Auto-resolve (preset)" badge (advisory only —
                    // never auto-applies; HARD-RULE-9). When the
                    // group is matched, the row carries a subtle
                    // dimmed background so the user's eye lands on
                    // the unmatched rows first.
                    const presetMatch = presetMatches.get(g.key);
                    // UFC-2 — disclosure for per-doc detail. Capped at
                    // max-h-64 with internal scroll so groups with 20+
                    // members don't blow the page layout (R5 mitigation).
                    return (
                      <li
                        key={g.key}
                        ref={assignFirst || isCrossLinkFocused ? firstRowRef : undefined}
                        className={`border border-border-subtle px-2 py-2 space-y-2 ${
                          presetMatch ? 'bg-state-hover/30' : 'bg-background'
                        }`}
                        data-testid={`evidence-conflicts-panel-row-${g.sectionId}-${g.fieldKey}`}
                        data-severity={g.worstSeverity}
                        data-group-key={g.key}
                        data-member-count={g.members.length}
                        data-preset-matched={presetMatch ? 'true' : 'false'}
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className={`inline-flex items-center justify-center px-1.5 py-0.5 type-body uppercase tracking-widest border shrink-0 ${SEVERITY_CHIP[g.worstSeverity]}`}
                            aria-label={`${g.worstSeverity} severity`}
                          >
                            {g.worstSeverity}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-bold text-text-primary truncate">
                              {g.fieldLabel}
                            </div>
                            <div
                              className="text-[10px] text-text-secondary leading-snug"
                              data-testid={`evidence-conflicts-panel-source-count-${g.sectionId}-${g.fieldKey}`}
                            >
                              {g.members.length} source{g.members.length === 1 ? '' : 's'} disagree
                            </div>
                            {/* UF-2 — preset-match badge. Surfaces
                                only when the active preset's rules
                                match this group. Advisory only —
                                clicking does nothing (HARD-RULE-9);
                                the user applies via the header CTA. */}
                            {presetMatch && (
                              <div
                                className="inline-flex items-center gap-1 mt-0.5 type-meta uppercase tracking-widest rounded-md border border-success/40 bg-success/5 text-success px-1 py-0.5"
                                data-testid={`evidence-conflicts-panel-preset-badge-${g.sectionId}-${g.fieldKey}`}
                                title={`Active preset will resolve this group via ${presetMatch.strategy}`}
                              >
                                Auto-resolve (preset)
                              </div>
                            )}
                          </div>
                          {/* UFC-3 — cross-link to the SED Variable
                              Browser row for this field. The parent
                              owns the navigation (sets focus + scrolls
                              the matching row); the panel just emits
                              the (sectionId, fieldKey) tuple. */}
                          {onViewVariable && (
                            <button
                              type="button"
                              onClick={() => onViewVariable(g.sectionId, g.fieldKey)}
                              data-testid={`evidence-conflicts-panel-view-variable-${g.sectionId}-${g.fieldKey}`}
                              className="shrink-0 type-body uppercase tracking-widest text-text-muted hover:text-primary inline-flex items-center gap-1 px-1.5 py-0.5"
                              aria-label={`View ${g.fieldLabel} in the variable browser`}
                            >
                              View variable
                              <ExternalLink className="w-2.5 h-2.5" aria-hidden="true" />
                            </button>
                          )}
                        </div>

                        {/* UFC-2 / ICR-2 — side-by-side value preview.
                            Provider column lists the ONE provider value
                            (every member in a group shares it).
                            Document column lists each member doc + value
                            (R5 — scrollable when 20+ entries). RDX-6 +
                            ICR-4 axis columns surface when the group's
                            members carry report / authored values. */}
                        <div
                          className={`grid gap-2 ${hasAuthored || hasReport ? 'grid-cols-3' : 'grid-cols-2'}`}
                          data-testid={`evidence-conflicts-panel-preview-${g.sectionId}-${g.fieldKey}`}
                        >
                          <div className="border-l-2 border-info pl-2">
                            <div className="type-meta uppercase tracking-widest text-text-muted">Provider</div>
                            <div
                              className="text-[10px] font-mono text-text-primary break-words"
                              data-testid={`evidence-conflicts-panel-provider-${g.sectionId}-${g.fieldKey}`}
                            >
                              {formatValue(g.providerValue)}
                            </div>
                            <div className="type-meta text-text-muted truncate">
                              {g.providerSource || 'API provider'}
                            </div>
                          </div>
                          {!hasAuthored && !hasReport && (
                            <div className="border-l-2 border-warning pl-2">
                              <div className="type-meta uppercase tracking-widest text-text-muted">
                                Documents ({g.members.length})
                              </div>
                              <ul
                                className="max-h-32 overflow-y-auto space-y-0.5"
                                data-testid={`evidence-conflicts-panel-doc-${g.sectionId}-${g.fieldKey}`}
                              >
                                {g.members.map(m => (
                                  <li
                                    key={m.docId || `${m.docName}-${formatValue(m.docValue)}`}
                                    className="text-[10px] font-mono text-text-primary break-words"
                                  >
                                    {formatValue(m.docValue)}
                                    <span className="text-text-muted font-sans">
                                      {' — '}{m.docName || 'extracted'}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {hasAuthored && (
                            <div className="border-l-2 border-success pl-2">
                              <div className="type-meta uppercase tracking-widest text-text-muted">Authored</div>
                              {g.members
                                .filter(m => m.authoredValue !== undefined)
                                .map(m => (
                                  <div
                                    key={`authored-${m.docId || m.authoredBy || formatValue(m.authoredValue)}`}
                                    className="text-[10px] font-mono text-text-primary break-words"
                                    data-testid={`evidence-conflicts-panel-authored-${g.sectionId}-${g.fieldKey}`}
                                  >
                                    {formatValue(m.authoredValue)}
                                    <span className="type-meta text-text-muted block truncate">
                                      {m.authoredBy ? `by ${m.authoredBy}` : 'user-authored'}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          )}
                          {hasReport && (
                            <div className="border-l-2 border-primary pl-2">
                              <div className="type-meta uppercase tracking-widest text-text-muted">Prior report</div>
                              {g.members
                                .filter(m => m.reportValue !== undefined)
                                .map(m => (
                                  <div
                                    key={`report-${m.docId || m.reportId || formatValue(m.reportValue)}`}
                                    className="text-[10px] font-mono text-text-primary break-words"
                                    data-testid={`evidence-conflicts-panel-report-${g.sectionId}-${g.fieldKey}`}
                                  >
                                    {formatValue(m.reportValue)}
                                    <span className="type-meta text-text-muted block truncate">
                                      {m.reportId ?? 'prior generated report'}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center flex-wrap justify-end gap-1.5">
                          {/* UFC-2 / ICR-2 — group-level one-click
                              Confirm when the underlying members all
                              point at `'provider'` / `'document'` as
                              authoritative. The atomic batch stamps
                              every member at once. */}
                          {showConfirmProvider && (
                            <button
                              type="button"
                              onClick={() => handleResolveGroup(g, 'prefer-provider')}
                              data-testid={`evidence-conflicts-panel-confirm-${g.sectionId}-${g.fieldKey}-provider`}
                              className="type-body uppercase tracking-widest rounded-md border border-success/50 bg-success/10 text-success hover:bg-success/20 px-1.5 py-0.5"
                            >
                              ✓ Confirm {g.fieldLabel} is {truncate(formatValue(g.providerValue), 24)}
                            </button>
                          )}
                          {showConfirmDoc && !hasMultipleDocValues && firstDocValue !== undefined && (
                            <button
                              type="button"
                              onClick={() => handleResolveGroup(g, 'prefer-doc')}
                              data-testid={`evidence-conflicts-panel-confirm-${g.sectionId}-${g.fieldKey}-doc`}
                              className="type-body uppercase tracking-widest rounded-md border border-success/50 bg-success/10 text-success hover:bg-success/20 px-1.5 py-0.5"
                            >
                              ✓ Use document: {truncate(formatValue(firstDocValue), 24)}
                            </button>
                          )}
                          {/* UFC-2 / R2 — "Use document…" picker. Only
                              renders when the group has ≥2 distinct doc
                              values; forces the user to choose WHICH
                              doc value wins instead of silently picking
                              the first. */}
                          {hasMultipleDocValues && (
                            <div className="relative shrink-0">
                              <button
                                type="button"
                                onClick={() => setOpenDocPicker(prev => prev === g.key ? null : g.key)}
                                disabled={isResolving}
                                data-testid={`evidence-conflicts-panel-use-doc-trigger-${g.sectionId}-${g.fieldKey}`}
                                className="type-body uppercase tracking-widest rounded-md border border-warning/50 bg-warning/5 text-warning hover:bg-warning/15 disabled:opacity-50 disabled:cursor-not-allowed px-1.5 py-0.5 inline-flex items-center gap-1"
                                aria-haspopup="menu"
                                aria-expanded={docPickerOpen}
                              >
                                Use document… <ChevronDown className="w-2.5 h-2.5" aria-hidden="true" />
                              </button>
                              {docPickerOpen && !isResolving && (
                                <div
                                  role="menu"
                                  className="absolute right-0 top-full mt-0.5 z-10 border border-border bg-background shadow-lg min-w-[200px] max-h-64 overflow-y-auto"
                                  data-testid={`evidence-conflicts-panel-use-doc-menu-${g.sectionId}-${g.fieldKey}`}
                                >
                                  {g.members
                                    .filter(m => m.docValue !== undefined && m.docValue !== '')
                                    .map(m => (
                                      <button
                                        key={m.docId || m.docName}
                                        type="button"
                                        role="menuitem"
                                        onClick={() => handleResolveGroup(g, 'prefer-doc', m.docId)}
                                        className="w-full text-left text-[10px] px-2 py-1 hover:bg-state-hover focus-visible:outline-none focus-visible:bg-state-hover"
                                        data-testid={`evidence-conflicts-panel-use-doc-${g.sectionId}-${g.fieldKey}-${m.docId || m.docName}`}
                                      >
                                        <span className="font-mono">{formatValue(m.docValue)}</span>
                                        <span className="text-text-muted"> — {m.docName || 'extracted'}</span>
                                      </button>
                                    ))}
                                </div>
                              )}
                            </div>
                          )}
                        <div className="relative shrink-0">
                          <button
                            type="button"
                            onClick={() => setOpenDropdown(prev => prev === g.key ? null : g.key)}
                            disabled={isResolving}
                            aria-haspopup="menu"
                            aria-expanded={dropdownOpen}
                            data-testid={`evidence-conflicts-panel-resolve-trigger-${g.sectionId}-${g.fieldKey}`}
                            className="type-body uppercase tracking-widest rounded-md border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed px-1.5 py-0.5 inline-flex items-center gap-1"
                          >
                            {isResolving ? 'Saving…' : (showConfirmProvider || showConfirmDoc) ? 'Review' : 'Resolve'} <ChevronDown className="w-2.5 h-2.5" aria-hidden="true" />
                          </button>
                          {dropdownOpen && !isResolving && (
                            <div
                              role="menu"
                              className="absolute right-0 top-full mt-0.5 z-10 border border-border bg-background shadow-lg min-w-[160px]"
                              data-testid={`evidence-conflicts-panel-resolve-menu-${g.sectionId}-${g.fieldKey}`}
                            >
                              {/* UFC-2 — dropdown stamps EVERY underlying
                                  member with the chosen resolution.
                                  prefer-doc lands on the picker when
                                  ≥2 docs disagree; otherwise it stamps
                                  every member as prefer-doc directly. */}
                              {((hasReport
                                ? ['prefer-provider', 'prefer-report', 'prefer-doc', 'manual-review']
                                : ['prefer-provider', 'prefer-doc', 'manual-review']) as ConflictResolution[]).map(opt => {
                                // Skip 'prefer-doc' when the group has
                                // no doc-side values at all (e.g. pure
                                // RDX-6 / ICR-4 3-axis groups).
                                if (opt === 'prefer-doc' && firstDocValue === undefined) return null;
                                return (
                                  <button
                                    key={opt}
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                      // R2 — when the user picks
                                      // "Use document" from the dropdown
                                      // and ≥2 doc values disagree,
                                      // route through the picker.
                                      if (opt === 'prefer-doc' && hasMultipleDocValues) {
                                        setOpenDropdown(null);
                                        setOpenDocPicker(g.key);
                                        return;
                                      }
                                      handleResolveGroup(g, opt);
                                    }}
                                    className="w-full text-left text-[10px] px-2 py-1 hover:bg-state-hover focus-visible:outline-none focus-visible:bg-state-hover"
                                    data-testid={`evidence-conflicts-panel-resolve-${g.sectionId}-${g.fieldKey}-${opt}`}
                                  >
                                    {RESOLVE_LABEL[opt]}
                                    {opt === 'prefer-doc' && hasMultipleDocValues && (
                                      <span className="ml-1 type-meta uppercase tracking-widest text-warning">(pick source)</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        </div>

                        {/* UFC-2 — per-doc disclosure. Capped scroll so
                            large groups don't blow the page (R5). */}
                        <details
                          open={detailOpen}
                          onToggle={e => {
                            const isOpen = (e.target as HTMLDetailsElement).open;
                            setExpandedGroupDetail(prev => {
                              const next = new Set(prev);
                              if (isOpen) next.add(g.key);
                              else next.delete(g.key);
                              return next;
                            });
                          }}
                          className="type-body text-text-muted"
                          data-testid={`evidence-conflicts-panel-detail-${g.sectionId}-${g.fieldKey}`}
                        >
                          <summary
                            className="cursor-pointer hover:text-text-primary"
                            data-testid={`evidence-conflicts-panel-detail-summary-${g.sectionId}-${g.fieldKey}`}
                          >
                            Show per-document detail
                          </summary>
                          <ul className="mt-1 ml-3 space-y-0.5 max-h-64 overflow-y-auto">
                            {g.members.map(m => (
                              <li
                                key={`detail-${m.docId || m.docName}`}
                                className="flex items-start gap-1.5"
                                data-testid={`evidence-conflicts-panel-detail-row-${g.sectionId}-${g.fieldKey}-${m.docId || m.docName}`}
                              >
                                <span
                                  className={`inline-block w-1 h-1 rounded-full mt-1.5 shrink-0 ${
                                    m.severity === 'high' ? 'bg-danger'
                                    : m.severity === 'medium' ? 'bg-warning'
                                    : 'bg-text-muted'
                                  }`}
                                  aria-label={m.severity}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-text-secondary">
                                    <span className="font-medium">{m.docName || m.docId || 'unknown doc'}</span>
                                    {': '}
                                    <span className="font-mono text-text-primary">{formatValue(m.docValue)}</span>
                                  </div>
                                  {m.explanation && (
                                    <div className="text-text-muted truncate" title={m.explanation}>
                                      {truncate(m.explanation, 100)}
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </details>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
